import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpXsrfTokenExtractor } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "./@auth/auth.service";
import { Router } from "@angular/router";
import { Observable, catchError, tap, throwError } from "rxjs";
import { AuthUtils } from "./utils/auth.utils";
import { CoreService } from "./services/core.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { CluLockscreenService } from "./utils/clu-lockscreen/clu-lockscreen.service";
import { AuthSessionDtoFO } from "./app.models";
import { AppService } from "./services/app.service";
import { TauriService } from "./services/tauri/tauri.service";

@Injectable()
export class AppInterceptor implements HttpInterceptor {
    constructor(
        private coreService: CoreService,
        private snackbar: MatSnackBar,
        private authService: AuthService,
        private router: Router,
        private dialogService: MatDialog,
        private xsrfTokenExtractor: HttpXsrfTokenExtractor,
        private _cluLockscreenService: CluLockscreenService,
        private _appService: AppService,
        private _tauriService: TauriService
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let newReq = req.clone();
        const authSession = this.authService.authenticatedSession$.value;

        if (!!authSession) {
            if (!AuthUtils.isTokenExpired(authSession.AccessToken)) {
                newReq = req.clone({
                    headers: req.headers.set('Authorization', 'Bearer ' + authSession.AccessToken),
                });
            }
            if (!!authSession.DOMAINE) {
                newReq = newReq.clone({ headers: newReq.headers.set('X-CLU-DOMAIN-ID', authSession.DOMAINE) });
            }
            if (!!authSession.INTERFACE_ID) {
                newReq = newReq.clone({ headers: newReq.headers.set('X-CLU-INTERFACE-ID', authSession.INTERFACE_ID.toString()) });
            }
        }

        const xsrfToken = this.xsrfTokenExtractor.getToken();
        if (!!xsrfToken && newReq.method == "POST") {
            newReq = newReq.clone({ withCredentials: true, headers: newReq.headers.set("X-XSRF-TOKEN", xsrfToken) });
        }

        if (this.coreService.getUIWorkspace() == 'be-workspace') {
            const siteGid = sessionStorage.getItem('CAURIS_LINKUP_BOP_SITE_GID');
            if (!!siteGid) {
                newReq = newReq.clone({ headers: newReq.headers.set("SITEGID", siteGid) });
            }
            if (this._appService.isBackendTestEnv) {
                newReq = newReq.clone({ headers: newReq.headers.set("X-CLU-TEST-ENV", "1") });
            }
        } else {
            if (this._appService.isFrontofficeTestEnv) {
                newReq = newReq.clone({ headers: newReq.headers.set("X-CLU-TEST-ENV", "1") });
            }
        }

        newReq = newReq.clone({ headers: newReq.headers.set("X-CLU-APP-VERSION", this._appService.appVersion$.value.APP_VERSION_ID) });
        if (this._tauriService.isTauri) {
          newReq = newReq.clone({ headers: newReq.headers.set("X-CLU-APP-TAURI", "1") });
        }

        return next.handle(newReq).pipe(
            tap(httpEvent => {
                if (httpEvent instanceof HttpResponse) {
                    if (httpEvent.headers.has("Refresh-Token")) {
                        const refreshToken = httpEvent.headers.get("Refresh-Token");
                        if (refreshToken != null) {
                            this.authService.AccessToken = refreshToken;
                        }
                    }
                }
            }),
            catchError((error) => {
                if ( error instanceof HttpErrorResponse && error.status === 401) {
                    const redirectUrl = this.coreService.getUIWorkspace() == 'be-workspace' ? "/cluadmin/login" : "/login";
                    this.authService.doRemoveAuthSession().subscribe(() => {
                        this.dialogService.closeAll();
                        this.snackbar.open("Session expirée", undefined, { duration: 3000 });
                        this.router.navigateByUrl(redirectUrl);
                    });
                }
                if ( error instanceof HttpErrorResponse && error.status === 400) {
                    if (!!error.error.InternalErrorCode) {
                        const internalErrorCore = error.error.InternalErrorCode;
                        switch(internalErrorCore) {
                            case 'APP_NOT_UPDATED':
                                this.authService.doRemoveAuthSession();
                                this.dialogService.closeAll();
                                this.router.navigateByUrl('/cluadmin/updates');
                                break;
                        }
                    }
                }
                return throwError(error);
            })
        );
    }
}
